/**
 * useKioskReset — Auto-reload the page after inactivity in kiosk mode.
 *
 * Listens for pointer, touch, and keyboard events. When no interaction
 * is detected for IDLE_TIMEOUT_MS, the page reloads — giving the next
 * dealer visitor a fresh start screen.
 *
 * This hook is a no-op when kioskMode is false.
 */
import { useEffect } from 'react';
import { useAppStore } from '../store/app.store';

/** Inactivity timeout before the kiosk reloads (ms). */
const IDLE_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes

const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  'pointerdown',
  'pointermove',
  'keydown',
  'touchstart',
  'wheel',
];

export function useKioskReset(): void {
  const kioskMode = useAppStore((s) => s.kioskMode);

  useEffect(() => {
    if (!kioskMode) return;

    let timer = window.setTimeout(() => window.location.reload(), IDLE_TIMEOUT_MS);

    const reset = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => window.location.reload(), IDLE_TIMEOUT_MS);
    };

    for (const evt of ACTIVITY_EVENTS) {
      document.addEventListener(evt, reset, { passive: true });
    }

    return () => {
      window.clearTimeout(timer);
      for (const evt of ACTIVITY_EVENTS) {
        document.removeEventListener(evt, reset);
      }
    };
  }, [kioskMode]);
}
